import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { LayoutTemplate, Loader2, Layers, Columns, LayoutGrid, AlertTriangle, Pencil, Monitor, Minimize2 } from 'lucide-react';
import { useWidgetEngine } from './hooks/useWidgetEngine';
import { useDashboardLayout } from './hooks/useDashboardLayout';
import { DashboardGrid } from './DashboardGrid';
import { WidgetManagerPanel } from './components/WidgetManagerPanel';
import { CustomizationWithSchema } from './components/WidgetCustomizationPanel';
import { findFirstAvailablePosition } from '../components/DashboardEngine/utils/gridEngine';
import { deleteWidget as deleteWidgetAPI } from './api/dashboardCatalog';
import type { DashboardPeriod, WidgetDefinition, UserWidgetPreference, DashboardFetcher } from './types';
import { LazyWidget } from './components/LazyWidget';
import type { WidgetRegistry } from './components/LazyWidget';
import { DashboardProvider } from './DashboardContext';
import type { WidgetAccessAdapter } from './access/types';
import { useDashboardTranslation } from './i18n';
import { usePresentationMode } from './utils/usePresentationMode';
import { DashboardCompanySelector, type DashboardCompany } from './components/DashboardCompanySelector';
import type { DashboardVocabularyTerm } from './types';

/**
 * Clave que agrupa los widgets sin categoría. Es una CLAVE, no un texto: antes era el literal
 * `'Otros'`, que se pintaba igual en inglés porque nunca pasó por el motor de traducción.
 *
 * Vive en el módulo y no dentro del componente: es una constante, y declararla en el cuerpo la
 * recreaba en cada render y la dejaba fuera de las deps de los `useMemo` que la usan.
 */
const UNCATEGORIZED = '__uncategorized__';

interface DashboardPageProps {
  /** Fetcher del host con autenticación y headers incluidos. */
  fetcher: DashboardFetcher;
  /** ID del usuario autenticado (del JWT del host). */
  userId: string;
  /** Roles del usuario autenticado (del JWT del host). */
  userRoles: string[];
  /** Muestra el selector de periodo (Hoy/Semana/Mes). Default false. */
  showPeriodSelector?: boolean;
  /** Permite crear widgets con endpoints externos en el Builder. Default false. */
  showExternalEndpoints?: boolean;
  /**
   * Si la puerta del Constructor existe, separada de si ofrece fuentes externas. Sin valor cae a
   * `showExternalEndpoints` — ningún host cambia de conducta. Ver `WidgetManagerPanel`.
   */
  showWidgetBuilder?: boolean;
  /** Registro custom del host: mapea widget_id → componente React para widgets específicos. */
  customWidgetRegistry?: WidgetRegistry;
  /** Orden preferido de categorías en la vista cascada. Las no listadas van al final alfabéticamente. */
  categoryOrder?: string[];
  /**
   * Vocabulario de segmentación que el Widget Builder ofrece al administrador, y que se guarda en
   * `roles_permitidos` del widget.
   *
   * Debe ser EL MISMO que el backend del host entrega como `roles`: el catálogo se filtra cruzando
   * ambos, así que si divergen el widget queda invisible para todos —incluido quien lo creó— sin
   * error ni log.
   *
   * <b>Si se omite, la lista sale VACÍA</b> y el Widget Builder lo dice. Antes caía a
   * `admin | ventas | compras | inventario` —el vocabulario de Siesa Comercial—, y un host de
   * Financiero o Nómina terminaba segmentando con módulos que no son suyos, sin ningún aviso.
   *
   * El `id` es lo que se persiste: puede ser el código de permiso del RBAC del host.
   */
  /**
   * 🔴 **RETIRADO el 2026-09-14 (F3.b.5).** Ver `WidgetManagerPanel`. El Constructor ya no pregunta
   * la vertical, así que esta prop no llegaba a ningún sitio.
   */
  /**
   * Vocabulario de categorías del host. `{ id, label }`: el `id` se persiste en `categoria` del
   * widget y el `label` es lo que se pinta — esa separación es lo que permite traducirlas.
   *
   * Admite `string[]` por compatibilidad: cada elemento se toma como `id` y `label` a la vez, que
   * es exactamente lo que hacía antes.
   */
  availableCategories?: Array<string | DashboardVocabularyTerm>;
  /**
   * Si el usuario administra el catálogo: puede borrar widgets que no sean semilla y —con
   * `showExternalEndpoints`— conectar APIs externas.
   *
   * <b>Lo decide el host, y por eso es una prop.</b> Hasta ahora el kit lo deducía buscando el
   * literal `'admin'` dentro de `userRoles`, que es justo lo que el resto de este módulo dejó de
   * hacer: un host cuyo rol de administración se llame `nomina_admin` o `administrador` no lo
   * activaba nunca. Y era una mina para el propio Comercial: el día que los ids pasen a ser códigos
   * de permiso (`…dashboard.admin`), el literal deja de coincidir y el Widget Builder pierde sus
   * funciones de administración <b>sin ningún error</b>.
   *
   * Si se omite, se conserva la deducción anterior por compatibilidad.
   */
  isAdmin?: boolean;

  /**
   * Capacidad de crear y editar definiciones de widget. Es lo que abre el Widget Builder.
   *
   * 🔴 Se separa de `isAdmin` porque son dos preguntas distintas y estaban colapsadas: un host
   * cuyo backend gatea la escritura con una capacidad propia —y no con el permiso de
   * administración— tenía la capacidad **inerte en la pantalla**, con el botón ausente para quien
   * el API sí autorizaba. Sin valor cae a `isAdmin` y nada cambia.
   */
  canWriteWidgets?: boolean;

  /**
   * Capacidad de cambiar la EXPOSICION de un widget: a quien alcanza (`es_privado`,
   * `roles_permitidos`) y con que autoridad se pide su dato (`is_internal`).
   *
   * 🔴 Separada tambien de `isAdmin`, y por un motivo mas duro que el de escribir: el backend la
   * exige en el PATCH del catalogo, asi que un asistente que ofrezca el paso de permisos a quien no
   * la tiene acaba en 403 -y, en la version anterior, mandaba `es_privado: true` sin decirlo y
   * volvia privado un widget de modulo-. Sin valor cae a `isAdmin`.
   */
  canPublishWidgets?: boolean;

  /**
   * 🔴 El adaptador del **diálogo de acceso** por widget: quién ve este indicador y qué exige para
   * verse. **Opcional**: sin él no se pinta ningún control de acceso y el tablero queda como estaba.
   *
   * <para>El kit no habla HTTP — recibe las operaciones y las llama. Quien las implementa contra su
   * API vive en el host, igual que `IWidgetAccessPolicy` en el backend del componente: el modelo de
   * acceso es del host, el formulario es del kit. Y todo lo que el diálogo ofrece sale de las
   * capacidades que el adaptador declara, así que un host con un modelo más pequeño obtiene un
   * formulario más pequeño en vez de casillas que su backend va a rechazar.</para>
   */
  accessAdapter?: WidgetAccessAdapter;
  /**
   * Titulo de la pantalla. El kit trae uno neutro ("Tablero de indicadores"); un host que quiera
   * llamarlo por su producto lo declara aqui — el kit no puede saber si es Comercial, Financiero o
   * Nomina, y hasta ahora decia "Dashboard Comercial" para todos.
   */
  title?: string;
  /**
   * Compañías entre las que el usuario puede cambiar.
   *
   * <b>Sin esta prop el tablero no dibuja selector</b>, y el host queda como estaba:
   * puede seguir poniendo el suyo por fuera. Es lo que permite adoptarlo sin migrar
   * de golpe.
   *
   * El tablero <b>no gestiona el alcance</b>: lo pinta y avisa del cambio. Quién es
   * la compañía activa, cómo se persiste y qué cabecera se envía siguen siendo del
   * host, que es quien tiene el contexto de sesión.
   */
  companies?: DashboardCompany[];
  /** La compañía activa. La decide el host — ver la advertencia de abajo. */
  selectedCompanyId?: string | null;
  /**
   * Se llama al elegir una compañía distinta.
   *
   * ⚠️ <b>El tablero NUNCA preselecciona por su cuenta.</b> Si no llega
   * `selectedCompanyId`, el chip muestra su texto de marcador y no se dispara nada.
   *
   * Es la lección de `MasterCrud`: allí el kit preseleccionaba una compañía por
   * defecto y <b>no</b> emitía el evento para esa elección silenciosa, así que host
   * y kit discrepaban sobre cuál era la activa — y hubo que agregar
   * `isSelectedByDefault` para salir del lío. Aquí no puede pasar: el kit no elige.
   */
  onCompanyChange?: (company: DashboardCompany) => void;
  /**
   * Opciones visibles pero no elegibles, con su motivo.
   * `{ '000': 'Las compañías habilitadas manejan monedas distintas' }`
   */
  companyDisabledReasons?: Record<string, string>;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
  fetcher,
  userId,
  userRoles,
  showPeriodSelector = false,
  showExternalEndpoints = false,
  showWidgetBuilder,
  customWidgetRegistry,
  categoryOrder,
  availableCategories,
  title,
  isAdmin,
  canWriteWidgets,
  canPublishWidgets,
  accessAdapter,
  companies,
  selectedCompanyId,
  onCompanyChange,
  companyDisabledReasons,
}) => {
  const { t } = useDashboardTranslation();

  /** Acepta las dos formas de `availableCategories` y deja una sola por dentro. */
  const categoryTerms = useMemo<DashboardVocabularyTerm[]>(
    () => (availableCategories ?? []).map(c => (typeof c === 'string' ? { id: c, label: c } : c)),
    [availableCategories],
  );

  /**
   * Clave → etiqueta visible. Si el host no declaró la categoría, se pinta la clave tal cual: es
   * lo que hacía antes y mantiene legibles los widgets creados con el modelo viejo, donde la clave
   * ERA el texto.
   */
  const categoryLabel = useCallback(
    (id: string) =>
      id === UNCATEGORIZED
        ? t('dashboard.category.uncategorized')
        : categoryTerms.find(c => c.id === id)?.label ?? id,
    [categoryTerms, t],
  );

  /** Solo los ids: es lo que consumen el Builder y el panel de personalización. */

  // 1. Motor Global (Catálogo, Perfil, Filtros)
  const esAdminDelCatalogo = isAdmin ?? userRoles.some(r => r.toLowerCase() === 'admin');
  const engine = useWidgetEngine({ userId, userRoles, fetcher, isAdmin: esAdminDelCatalogo });

  // 2. Layout Local (Sincronizado con BD vía Debounce)
  const layoutManager = useDashboardLayout({
    userId,
    initialLayout: engine.initialLayout,
    period: engine.period,
    fetcher,
  });

  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const [customizingWidget, setCustomizingWidget] = useState<{
    widget: WidgetDefinition;
    preference?: UserWidgetPreference;
  } | null>(null);
  /**
   * Las tres formas de organizar el tablero.
   *
   *  cascade  una seccion por categoria, una debajo de otra — todo de un vistazo
   *  tabs     una pestana por categoria — para trabajar concentrado en un modulo
   *  board    una sola grilla, sin encabezados — la vista de direccion
   *
   * `board` es nueva. NO reemplaza a la cascada: sustituirla habria borrado la
   * segmentacion visual por modulo, que es lo que hace visible el trabajo de
   * permisos — quien tiene ventas ve ventas.
   */
  const [viewMode, setViewMode] = useState<'cascade' | 'tabs' | 'board'>('cascade');
  // Vacio: la primera categoria REAL lo corrige en el efecto de abajo. Antes arrancaba en
  // 'Ventas', que en un host de Financiero o Nomina no existe — se auto-corregia, pero dejaba una
  // categoria de otro producto escrita en el estado inicial de una libreria compartida.
  const [activeTab, setActiveTab] = useState<string>('');
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  // --- Lógica de Agrupación por Categorías ---
  const layoutByCategory = useMemo(() => {
    const groups: Record<string, UserWidgetPreference[]> = {};
    
    layoutManager.layout.forEach(pref => {
      const def = engine.catalog.find(c => c.widget_id === pref.widget_id);
      if (def) {
        const cat = def.categoria || UNCATEGORIZED;
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(pref);
      }
    });
    
    return groups;
  }, [layoutManager.layout, engine.catalog]);

  const sortedCategories = useMemo(() => {
    const categories = Object.keys(layoutByCategory);
    // Sin `categoryOrder` no hay prioridad: se pintan alfabeticamente. El fallback anterior era
    // ['Ventas','Compras','Inventario'] — inofensivo (se filtra por las que existen) pero era
    // vocabulario de Comercial dentro del kit.
    const prioritized = (categoryOrder ?? []).filter(c => categories.includes(c));
    categories.sort().forEach(c => { if (!prioritized.includes(c)) prioritized.push(c); });
    return prioritized;
  }, [layoutByCategory, categoryOrder]);

  /**
   * Modo presentación: el tablero a pantalla completa, para dejarlo en un monitor.
   *
   * Usa la Fullscreen API y no un overlay propio porque lo que hay que ocultar
   * —navegación, breadcrumb, selector de compañía— es del HOST, no de este
   * componente: el kit no los dibuja ni sabe que existen. Ver `usePresentationMode`.
   */
  /** Las categorías, por referencia, para que la rotación no reinicie su temporizador
      en cada render — depender del array lo recrearía constantemente. */
  const categoriesRef = useRef<string[]>([]);
  categoriesRef.current = sortedCategories;

  const handleRotate = useCallback((index: number) => {
    const next = categoriesRef.current[index];
    if (next) {
      setViewMode('tabs');
      setActiveTab(next);
    }
  }, []);

  /**
   * Layout de la vista de tablero único.
   *
   * <b>Se recalculan las posiciones, no se reutilizan las guardadas.</b> Las
   * coordenadas persistidas se calcularon POR CATEGORÍA, y cada categoría empieza en
   * `y = 0`: juntarlas en una sola grilla dejaba widgets superpuestos y huecos
   * enormes entre bloques que en su categoría eran contiguos.
   *
   * Aquí se reordena en el orden en que el usuario los tiene y se acomodan de
   * izquierda a derecha, saltando de fila cuando no cabe el siguiente. La compactación
   * vertical de la grilla termina de cerrar los huecos.
   *
   * <b>Y se descartan las preferencias sin definición en el catálogo</b>, en vez de
   * pintar "Widget desconocido": eso pasa cuando un widget se desactiva del catálogo
   * o el usuario pierde el permiso que lo concedía, y en los dos casos lo correcto es
   * que desaparezca en silencio — no dejar una lápida en el tablero.
   */
  const boardLayout = useMemo(() => {
    const COLS = 12;
    let x = 0;
    let y = 0;
    let rowHeight = 0;

    return layoutManager.layout
      .filter(pref => engine.catalog.some(c => c.widget_id === pref.widget_id))
      .map(pref => {
        const w = Math.min(pref.col_span || 3, COLS);
        if (x + w > COLS) {
          x = 0;
          y += rowHeight || 1;
          rowHeight = 0;
        }
        const placed = { ...pref, pos_x: x, pos_y: y };
        x += w;
        rowHeight = Math.max(rowHeight, pref.row_span || 1);
        return placed;
      });
  }, [layoutManager.layout, engine.catalog]);

  const presentation = usePresentationMode({
    onRotate: handleRotate,
    rotatableCount: sortedCategories.length,
  });

  // Categorías únicas del catálogo completo (para el panel de personalización)
  const catalogCategories = useMemo(() =>
    [...new Set(engine.catalog.map(w => w.categoria).filter(Boolean))].sort(),
    [engine.catalog]
  );

  useEffect(() => {
    if (sortedCategories.length > 0 && !sortedCategories.includes(activeTab)) {
      setActiveTab(sortedCategories[0]);
    }
  }, [sortedCategories, activeTab]);

  const handleCategoryLayoutChange = (category: string, newCategoryLayout: UserWidgetPreference[]) => {
    const otherCategoriesLayout = layoutManager.layout.filter(pref => {
      const def = engine.catalog.find(c => c.widget_id === pref.widget_id);
      const cat = def ? (def.categoria || UNCATEGORIZED) : UNCATEGORIZED;
      return cat !== category;
    });
    layoutManager.updateLayout([...otherCategoriesLayout, ...newCategoryLayout]);
  };

  // Manejador para quitar un widget — solicita confirmación antes de eliminar
  const handleRemoveWidget = (instanceId: string) => {
    setPendingRemoveId(instanceId);
  };

  const confirmRemove = () => {
    if (pendingRemoveId) {
      layoutManager.updateLayout(layoutManager.layout.filter(w => w.id !== pendingRemoveId));
      setPendingRemoveId(null);
    }
  };


  if (engine.isLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-screen"
        style={{ background: 'var(--dash-surface-page)' }}
      >
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: 'var(--dash-accent)' }} />
        <p className="font-medium" style={{ color: 'var(--dash-text-secondary)' }}>{t('dashboard.chrome.loading')}</p>
      </div>
    );
  }

  if (engine.initError) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-screen p-8"
        style={{ background: 'var(--dash-surface-page)' }}
      >
        <AlertTriangle className="w-12 h-12 mb-4" style={{ color: 'var(--dash-danger)' }} />
        <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--dash-text-primary)' }}>{t('dashboard.chrome.loadError')}</h2>
        <p className="text-sm text-center max-w-md" style={{ color: 'var(--dash-text-secondary)' }}>{engine.initError}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 px-4 py-2 text-white text-sm font-medium rounded-lg transition-opacity hover:opacity-90"
          style={{ background: 'var(--dash-accent)' }}
        >
          {t('dashboard.chrome.retry')}
        </button>
      </div>
    );
  }

  return (
    <DashboardProvider fetcher={fetcher} catalog={engine.catalog} accessAdapter={accessAdapter}>
    <div
      ref={presentation.containerRef}
      className={`min-h-screen overflow-x-hidden relative ${presentation.isActive ? 'dash-presentation' : ''}`}
      /*
        Los tokens van INLINE y no en una clase: `bg-[#F4F6F8]` se emite en `@layer utilities` y
        `.dash-presentation` vive en `@layer base`, así que la utilidad ganaba siempre y el fondo
        del tablero se quedaba claro en modo oscuro (y la declaración `background` de
        `.dash-presentation` era código muerto).
      */
      style={{ background: 'var(--dash-surface-page)', color: 'var(--dash-text-primary)' }}
    >
      {/* 1. Topbar Estricta según PRD */}
      {/*
        Alto MINIMO, no fijo: con el chip de compania bajo el titulo, 64px no dan
        y el chip queda pegado al borde inferior. Con min-h la barra crece solo
        cuando hay chip, y se ve igual que antes cuando no lo hay.
      */}
      <header
        className="sticky top-0 z-40 border-b shadow-sm min-h-[64px] py-2.5 px-6 flex items-center justify-between gap-4"
        style={{ background: 'var(--dash-surface-card)', borderColor: 'var(--dash-border)' }}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'var(--dash-accent)' }}
          >
            <LayoutTemplate className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-col gap-2 py-1.5">
            <h1 className="text-xl font-bold tracking-tight">{title ?? t('dashboard.chrome.title')}</h1>
            {/*
              El chip va BAJO el título, dentro de la barra del tablero — no en una
              franja propia por encima de la pantalla. Es el mismo control que el
              maestro de Ítems, para que cambiar de compañía sea el mismo gesto en
              las dos pantallas.

              Sólo aparece si el host pasa compañías: sin la prop, el tablero se
              comporta como antes y el host puede seguir con el suyo por fuera.
            */}
            {companies && companies.length > 0 && onCompanyChange && (
              <DashboardCompanySelector
                companies={companies}
                selectedCompanyId={selectedCompanyId}
                onCompanyChange={onCompanyChange}
                optionDisabledReasons={companyDisabledReasons}
              />
            )}
          </div>
          
          {layoutManager.isSaving && (
            <span
              className="ml-4 flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full"
              style={{ color: 'var(--dash-text-secondary)', background: 'var(--dash-surface-page)' }}
            >
              <Loader2 className="w-3 h-3 animate-spin" /> {t('dashboard.chrome.saving')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/*
            Selector de vista — solo si hay MAS DE UNA categoria con widgets.

            Con un solo modulo activo las tres vistas producen exactamente la misma
            pantalla, y un control que no cambia nada es ruido.
          */}
          {sortedCategories.length > 1 && (
            <>
              <div className="flex items-center p-1 rounded-lg" style={{ background: 'var(--dash-surface-page)' }}>
                {([
                  ['cascade', Layers, 'dashboard.chrome.viewCascade'],
                  ['tabs', Columns, 'dashboard.chrome.viewTabs'],
                  ['board', LayoutGrid, 'dashboard.chrome.viewBoard'],
                ] as const).map(([mode, Icon, labelKey]) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className="p-1.5 rounded transition-all"
                    style={viewMode === mode
                      ? { background: 'var(--dash-surface-card)', color: 'var(--dash-accent)', boxShadow: '0 1px 2px rgba(16,24,40,.06)' }
                      : { color: 'var(--dash-text-muted)' }}
                    title={t(labelKey)}
                    aria-pressed={viewMode === mode}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>

              <div className="w-px h-8 mx-1" style={{ background: 'var(--dash-border)' }}></div>
            </>
          )}

          {/* Modo presentación */}
          {presentation.isActive ? (
            <div className="flex items-center gap-3">
              {/*
                Sin selector de frecuencia: cada widget ya se refresca con su propia
                `refresh_policy`. Un temporizador global dispararia los ocho a la vez
                y pisaria el ritmo que cada indicador tiene configurado.
              */}
              {sortedCategories.length > 1 && (
                <label className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--dash-text-secondary)' }}>
                  <input
                    type="checkbox"
                    checked={presentation.isRotating}
                    onChange={e => presentation.setRotating(e.target.checked)}
                  />
                  {t('dashboard.presentation.rotate')}
                </label>
              )}

              <button
                onClick={presentation.exit}
                className="p-1.5 rounded transition-colors"
                style={{ color: 'var(--dash-text-secondary)' }}
                title={t('dashboard.presentation.exit')}
                aria-label={t('dashboard.presentation.exit')}
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={presentation.enter}
              className="p-1.5 rounded transition-colors"
              style={{ color: 'var(--dash-text-muted)' }}
              title={t('dashboard.presentation.enter')}
              aria-label={t('dashboard.presentation.enter')}
            >
              <Monitor className="w-4 h-4" />
            </button>
          )}

          {/* Selector de Tiempo (Filtro Global) */}
          {showPeriodSelector && (
            <div className="p-1 rounded-lg flex items-center" style={{ background: 'var(--dash-surface-page)' }}>
              {(['Hoy', 'Semana', 'Mes'] as DashboardPeriod[]).map(p => (
                <button
                  key={p}
                  onClick={() => engine.setPeriod(p)}
                  className="px-4 py-1.5 text-sm font-medium rounded-md transition-all"
                  style={engine.period === p
                    ? { background: 'var(--dash-surface-card)', color: 'var(--dash-accent)', boxShadow: '0 1px 2px rgba(16,24,40,.06)' }
                    : { color: 'var(--dash-text-muted)' }}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <div className="w-px h-8 mx-2" style={{ background: 'var(--dash-border)' }}></div>

          {/* Botón Editar — abre sidebar (= activa edit mode) */}
          <button
            onClick={() => setIsManagerOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm border transition-colors hover:opacity-90"
            style={isManagerOpen
              ? { background: 'var(--dash-accent)', borderColor: 'var(--dash-accent)', color: '#ffffff' }
              : { background: 'var(--dash-surface-card)', borderColor: 'var(--dash-border-strong)', color: 'var(--dash-text-primary)' }}
          >
            <Pencil className="w-4 h-4" /> {t('dashboard.chrome.edit')}
          </button>
        </div>
      </header>

      {/* Banner de Modo Edición — visible cuando el sidebar está abierto */}
      {isManagerOpen && (
        <div
          className="border-b px-6 py-2 flex items-center justify-center"
          style={{ background: 'var(--dash-bg-accent)', borderColor: 'var(--dash-border)' }}
        >
          <p className="text-sm font-medium" style={{ color: 'var(--dash-accent)' }}>
            {t('dashboard.chrome.editModeHint')}
          </p>
        </div>
      )}

      {/* Menú de Pestañas (SubHeader) */}
      {viewMode === 'tabs' && sortedCategories.length > 0 && (
        <div
          className="px-6 pt-4 border-b flex gap-6"
          style={{ background: 'var(--dash-surface-card)', borderColor: 'var(--dash-border)' }}
        >
          {sortedCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className="pb-3 font-bold text-sm tracking-wide transition-colors border-b-2"
              style={activeTab === cat
                ? { borderColor: 'var(--dash-accent)', color: 'var(--dash-accent)' }
                : { borderColor: 'transparent', color: 'var(--dash-text-muted)' }}
            >
              {categoryLabel(cat).toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* 2. Contenido Principal (Grid) */}
      <main className={`p-6 transition-all duration-300 ${isManagerOpen ? 'mr-[348px]' : ''}`}>
        {/*
          Vista de tablero unico: una sola grilla con TODO, sin encabezados de
          categoria. El layout se guarda igual que en las otras vistas — es la
          misma coleccion de preferencias, presentada junta.
        */}
        {viewMode === 'board' && boardLayout.length > 0 && (
          <DashboardGrid
            layout={boardLayout}
            catalog={engine.catalog}
            isEditMode={isManagerOpen}
            globalFilters={{ period: engine.period }}
            onLayoutChange={layoutManager.updateLayout}
            onRemoveWidget={handleRemoveWidget}
            renderWidget={(pref, def, editMode, filters, onRemove) => (
              <LazyWidget
                preference={pref}
                definition={def}
                isEditMode={editMode}
                period={filters.period as DashboardPeriod}
                onRemove={onRemove}
                customWidgetRegistry={customWidgetRegistry}
              />
            )}
          />
        )}

        {viewMode !== 'board' && sortedCategories.map(category => {
          // Si estamos en modo tabs y no es la activa, no renderizamos su grid
          if (viewMode === 'tabs' && category !== activeTab) return null;

          const categoryLayout = layoutByCategory[category];

          return (
            <div key={category} className="mb-10">
              {viewMode === 'cascade' && (
                <h2
                  className="text-sm font-bold mb-4 uppercase tracking-wider pl-3 border-l-4 flex items-center gap-2"
                  style={{ color: 'var(--dash-accent)', borderColor: 'var(--dash-accent)' }}
                >
                  <span className="font-normal" style={{ color: 'var(--dash-text-muted)' }}>|</span> {categoryLabel(category)}
                </h2>
              )}
              
              <DashboardGrid
                layout={categoryLayout}
                catalog={engine.catalog}
                isEditMode={isManagerOpen}
                globalFilters={{ period: engine.period }}
                onLayoutChange={(newLayout) => handleCategoryLayoutChange(category, newLayout)}
                onRemoveWidget={handleRemoveWidget}
                renderWidget={(pref, def, editMode, filters, onRemove) => (
                  <LazyWidget
                    preference={pref}
                    definition={def}
                    isEditMode={editMode}
                    period={filters.period as DashboardPeriod}
                    onRemove={onRemove}
                    customWidgetRegistry={customWidgetRegistry}
                  />
                )}
              />
            </div>
          );
        })}

        {sortedCategories.length === 0 && (
          <div
            className="flex flex-col items-center justify-center mt-20 p-8 border border-dashed rounded-xl max-w-md mx-auto"
            style={{ background: 'var(--dash-surface-card)', borderColor: 'var(--dash-border-strong)', color: 'var(--dash-text-secondary)' }}
          >
            <LayoutTemplate className="w-12 h-12 mb-4" style={{ color: 'var(--dash-text-muted)' }} />
            <h3 className="font-semibold mb-1" style={{ color: 'var(--dash-text-primary)' }}>{t('dashboard.chrome.empty')}</h3>
            <p className="text-center text-sm">{t('dashboard.chrome.emptyHint')}</p>
          </div>
        )}
      </main>

      {/* 3. Panel Lateral (Catálogo) */}
      <WidgetManagerPanel
        // 🔴 F3 — los TÉRMINOS, no los ids. Antes viajaban `categoryIds`, y con eso el Constructor
        // perdía dos cosas en el salto: la etiqueta traducida (pintaba la clave, que en Comercial
        // coincide por casualidad) y —lo que importa— la VERTICAL. Sin ella no hay nada que derivar.
        availableCategories={categoryTerms}
        isOpen={isManagerOpen}
        catalog={engine.catalog}
        currentLayout={layoutManager.layout}
        isAdmin={esAdminDelCatalogo}
        canWriteWidgets={canWriteWidgets}
        canPublishWidgets={canPublishWidgets}
        userRoles={userRoles}
        userId={userId}
        showExternalEndpoints={showExternalEndpoints}
        showWidgetBuilder={showWidgetBuilder}
        onClose={() => setIsManagerOpen(false)}
        onRequestAdd={(widget) => {
          setCustomizingWidget({ widget, preference: undefined });
          setIsManagerOpen(false);
        }}
        onRequestModify={(widget, preference) => {
          setCustomizingWidget({ widget, preference });
          setIsManagerOpen(false);
        }}
        onRemoveWidget={handleRemoveWidget}
        onWidgetCreated={(newDef) => {
          engine.addToCatalog(newDef);
          // Si es privado, agregar directamente al layout del usuario
          if (newDef.es_privado) {
            const pos = findFirstAvailablePosition(
              layoutByCategory[newDef.categoria] ?? [],
              newDef.cols_default,
              newDef.altura_default
            );
            layoutManager.updateLayout([...layoutManager.layout, {
              id: `inst_${crypto.randomUUID()}`,
              user_id: userId,
              widget_id: newDef.widget_id,
              activo: true,
              orden: layoutManager.layout.length + 1,
              col_span: newDef.cols_default,
              /**
               * `altura_default` del catálogo está expresado en filas de `LEGACY_ROW_HEIGHT`
               * (148 px), así que la preferencia nace SIN sello y `migrateLayout` la convierte
               * como a cualquier otra heredada. El día que el catálogo pase a las filas de
               * `ROW_HEIGHT` (80 px), aquí hay que estampar `layoutVersion: LAYOUT_VERSION`.
               */
              row_span: newDef.altura_default,
              pos_x: pos.x,
              pos_y: pos.y,
              modificado_en: new Date().toISOString(),
            }]);
          }
        }}
        onWidgetUpdated={(updatedDef) => {
          engine.updateInCatalog(updatedDef);
        }}
        onDeleteWidget={async (widgetId) => {
          await deleteWidgetAPI(widgetId, fetcher);
          engine.removeFromCatalog(widgetId);
          const cleaned = layoutManager.layout.filter(p => p.widget_id !== widgetId);
          if (cleaned.length !== layoutManager.layout.length) {
            layoutManager.updateLayout(cleaned);
          }
        }}
      />

      {/* 4. Panel de Personalización unificado (agregar o modificar) */}
      {customizingWidget && (
        <CustomizationWithSchema
          widget={customizingWidget.widget}
          preference={customizingWidget.preference}
          availableCategories={catalogCategories}
          period={engine.period}
          onConfirm={(overrides) => {
            if (customizingWidget.preference) {
              // Modo personalize: actualizar config_json de la preferencia existente
              const updated = layoutManager.layout.map(p =>
                p.id === customizingWidget.preference!.id
                  ? { ...p, config_json: Object.keys(overrides).length > 0 ? overrides : undefined }
                  : p
              );
              layoutManager.updateLayout(updated);
              setCustomizingWidget(null);
            } else {
              // Modo add: crear nueva instancia en el layout
              const def = customizingWidget.widget;
              const cols = (overrides.cols_default as number) ?? def.cols_default;
              const pos = findFirstAvailablePosition(
                layoutByCategory[def.categoria] ?? [],
                cols,
                def.altura_default
              );
              layoutManager.updateLayout([...layoutManager.layout, {
                id: `inst_${crypto.randomUUID()}`,
                user_id: userId,
                widget_id: def.widget_id,
                activo: true,
                orden: layoutManager.layout.length + 1,
                col_span: cols,
                // Ver la nota de `onWidgetCreated`: `altura_default` está en filas de 148 px.
                row_span: def.altura_default,
                pos_x: pos.x,
                pos_y: pos.y,
                config_json: Object.keys(overrides).length > 0 ? overrides : undefined,
                modificado_en: new Date().toISOString(),
              }]);
              setCustomizingWidget(null);
              setIsManagerOpen(true);   // volver al catálogo para seguir agregando
            }
          }}
          onRestore={customizingWidget.preference ? () => {
            const restored = layoutManager.layout.map(p =>
              p.id === customizingWidget.preference!.id
                // "Restaurar" borra los overrides del usuario, NO el sello de layout: si se
                // fuera con ellos, la migración volvería a convertir un row_span ya convertido.
                ? { ...p, config_json: p.config_json?.layoutVersion !== undefined
                      ? { layoutVersion: p.config_json.layoutVersion }
                      : undefined }
                : p
            );
            layoutManager.updateLayout(restored);
            setCustomizingWidget(null);
          } : undefined}
          onClose={() => {
            const wasAdd = !customizingWidget.preference;
            setCustomizingWidget(null);
            if (wasAdd) setIsManagerOpen(true);  // volver al catálogo si era modo add
          }}
        />
      )}

      {/*
        5. Confirmación de quitar un widget.

        <b>Baja de tono, a propósito.</b> Usaba el lenguaje de "vas a destruir
        datos" —círculo rojo, triángulo de alerta, botón rojo, "no se puede
        deshacer"— para una acción que sólo esconde un widget del tablero PROPIO
        del usuario y que se revierte desde el catálogo en dos clics. La gravedad
        era falsa, y una alarma que no corresponde enseña a ignorar las que sí.

        Se conserva la confirmación (quitar sigue siendo un clic sin vuelta atrás
        inmediata). Lo que se retira es el rojo, el icono de alerta y el texto que
        prometía un daño que no existe.

        Y deja de estar a medio traducir: el párrafo y los dos botones estaban
        cableados en español mientras el título usaba `t()`.
      */}
      {pendingRemoveId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
          <div
            className="rounded-xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in duration-150"
            style={{ background: 'var(--dash-surface-card)' }}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-base font-medium mb-1" style={{ color: 'var(--dash-text-primary)' }}>
              {t('dashboard.confirm.removeQuestion')}
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--dash-text-secondary)' }}>
              {t('dashboard.confirm.removeBody')}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setPendingRemoveId(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors hover:opacity-80"
                style={{ color: 'var(--dash-text-secondary)', background: 'var(--dash-surface-page)' }}
              >
                {t('dashboard.confirm.cancel')}
              </button>
              <button
                onClick={confirmRemove}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors hover:opacity-90"
                style={{ background: 'var(--dash-accent)' }}
              >
                {t('dashboard.confirm.removeAction')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DashboardProvider>
  );
};

